import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiDownload, FiPrinter } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import PageLayout from '../../components/PageLayout';
import {
  FiltersCard, FilterGrid, FilterItem, FilterLabel, FilterSelect,
  KPIGrid, KPI, KL, KV, ToolbarRow, ExportBtn, ReportCard, RTable, Loading, Empty,
} from './reportStyles';
import { loadAssetReportData, exportRowsToExcel, printAssetReport, computeFixedAssetMovement } from '../../utils/assetReportHelper';

// FBR-style Written-Down-Value (WDV) depreciation statement, grouped by block
// (category). Opening WDV + Additions − Depreciation = Closing WDV.
const FBRDepreciationStatement = () => {
  const { activeCompany, activeBranch, activeFinancialYear } = useAuth();
  const { formatCurrency } = useCurrency();
  const [data, setData] = useState({ assets: [], depRuns: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('fy');

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try { setData(await loadAssetReportData(activeCompany.id, activeBranch)); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [activeCompany, activeBranch]);
  useEffect(() => { load(); }, [load]);

  const fyStart = scope === 'fy' ? (activeFinancialYear?.startDate || null) : null;
  const fyEnd = scope === 'fy' ? (activeFinancialYear?.endDate || null) : null;

  // Tax statement stays on original cost: Asset Appreciation is left out.
  const { rows, totals } = useMemo(
    () => computeFixedAssetMovement(data.assets, data.depRuns, fyStart, fyEnd, { costBasis: true }),
    [data, fyStart, fyEnd]
  );

  const rateFor = (categoryId) => {
    const cat = data.categories.find(c => c.id === categoryId);
    if (!cat) return '';
    return cat.defaultMethod === 'reducing_balance' ? `${cat.defaultRate || 0}%` : (cat.defaultMethod === 'none' ? '—' : `${cat.defaultUsefulLifeYears || 0}yr SLM`);
  };

  const columns = [
    { key: 'Block', label: 'Asset Block' }, { key: 'Rate', label: 'Rate' },
    { key: 'Opening', label: 'Opening WDV', r: true }, { key: 'Additions', label: 'Additions', r: true },
    { key: 'Depreciation', label: 'Depreciation', r: true }, { key: 'Closing', label: 'Closing WDV', r: true },
  ];
  const buildRows = () => rows.map(c => ({
    Block: c.categoryName, Rate: rateFor(c.categoryId),
    Opening: formatCurrency(c.openingNBV), Additions: formatCurrency(c.additions),
    Depreciation: formatCurrency(c.depreciation), Closing: formatCurrency(c.closingNBV),
  }));
  const scopeLabel = scope === 'fy' ? `FY ${fyStart || ''} → ${fyEnd || ''}` : 'All time';

  const onPrint = () => printAssetReport({
    company: activeCompany, branch: activeBranch && activeBranch.id !== 'all' ? activeBranch : null,
    title: 'FBR Depreciation Statement (WDV)', filters: scopeLabel, columns, rows: buildRows(),
    totals: { Opening: formatCurrency(totals.openingNBV), Additions: formatCurrency(totals.additions), Depreciation: formatCurrency(totals.depreciation), Closing: formatCurrency(totals.closingNBV) },
    kpis: [
      { label: 'Opening WDV', value: formatCurrency(totals.openingNBV) },
      { label: 'Depreciation', value: formatCurrency(totals.depreciation) },
      { label: 'Closing WDV', value: formatCurrency(totals.closingNBV), highlight: true },
    ],
    footerNote: 'WDV basis (Third Schedule). Disposals within the year may require manual adjustment.',
  });

  return (
    <PageLayout pageTitle="FBR Depreciation Statement" breadcrumb="Reports / Fixed Assets / FBR Statement">
      <FiltersCard>
        <FilterGrid>
          <FilterItem>
            <FilterLabel>Period</FilterLabel>
            <FilterSelect value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="fy">Active Financial Year</option>
              <option value="all">All time</option>
            </FilterSelect>
          </FilterItem>
        </FilterGrid>
      </FiltersCard>

      <KPIGrid>
        <KPI><KL>Opening WDV</KL><KV>{formatCurrency(totals.openingNBV)}</KV></KPI>
        <KPI><KL>Additions</KL><KV>{formatCurrency(totals.additions)}</KV></KPI>
        <KPI><KL>Depreciation</KL><KV>{formatCurrency(totals.depreciation)}</KV></KPI>
        <KPI $highlight><KL>Closing WDV</KL><KV>{formatCurrency(totals.closingNBV)}</KV></KPI>
      </KPIGrid>

      <ToolbarRow>
        <h3 style={{ color: colors.text.secondary }}>{scopeLabel} · Opening WDV + Additions − Depreciation = Closing WDV</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportBtn onClick={() => exportRowsToExcel('FBR_Depreciation', 'WDV', buildRows())}><FiDownload /> Excel</ExportBtn>
          <ExportBtn onClick={onPrint}><FiPrinter /> Print</ExportBtn>
        </div>
      </ToolbarRow>

      {loading ? <Loading>Loading...</Loading> : rows.length === 0 ? <Empty>No depreciable blocks for this period.</Empty> : (
        <ReportCard>
          <RTable>
            <thead>
              <tr>
                <th>Asset Block</th><th>Rate</th><th className="r">Opening WDV</th>
                <th className="r">Additions</th><th className="r">Depreciation</th><th className="r">Closing WDV</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.categoryId || 'uncat'}>
                  <td>{c.categoryName}</td>
                  <td>{rateFor(c.categoryId)}</td>
                  <td className="r">{formatCurrency(c.openingNBV)}</td>
                  <td className="r">{formatCurrency(c.additions)}</td>
                  <td className="r">{formatCurrency(c.depreciation)}</td>
                  <td className="r">{formatCurrency(c.closingNBV)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Total</td>
                <td className="r">{formatCurrency(totals.openingNBV)}</td>
                <td className="r">{formatCurrency(totals.additions)}</td>
                <td className="r">{formatCurrency(totals.depreciation)}</td>
                <td className="r">{formatCurrency(totals.closingNBV)}</td>
              </tr>
            </tfoot>
          </RTable>
        </ReportCard>
      )}
    </PageLayout>
  );
};

export default FBRDepreciationStatement;
